import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';

// ─────────────────────────────────────────────────────────────────────────────
// The class game: a Kahoot-style quiz the teacher hosts live. It runs on the
// class's private broadcast channel quiz:<class> (20261024_class_game.sql).
//
//   teacher → everyone   'state'   what's on screen now (lobby, a question, the
//                                   answer, the leaderboard, the podium)
//   student → teacher    'join'    "I'm here"; the teacher answers with the state
//   student → teacher    'answer'  their pick for a question
//
// The teacher's page keeps the scores; nothing is stored in the database.
// ─────────────────────────────────────────────────────────────────────────────

export type QuizSource = 'grammar' | 'story' | 'vocab';

export type QuizQuestion = {
  source: QuizSource;
  /** Small line above, e.g. "Which is the right translation?" */
  instruction: string;
  /** The big line ("___" is a blank). */
  prompt: string;
  /** Under the prompt, e.g. "to speak". */
  hint?: string;
  /** The prompt's language (for reading it aloud). */
  lang?: string;
  options: string[];
  correct: number;
};

export type Ranked = { id: string; score: number; delta: number; streak: number };

export type QuizState =
  | { phase: 'lobby'; game: string; title: string }
  | { phase: 'question'; game: string; i: number; total: number; q: Omit<QuizQuestion, 'correct'>; limit: number; left: number; answered: number; players: number }
  | { phase: 'reveal'; game: string; i: number; total: number; q: QuizQuestion; counts: number[];
      /** Each player's result for this question. */
      results: Record<string, { right: boolean; points: number; score: number; rank: number; streak: number }> }
  | { phase: 'leaderboard'; game: string; i: number; total: number; top: Ranked[] }
  | { phase: 'end'; game: string; top: Ranked[]; ranks: Record<string, { rank: number; score: number }> }
  | { phase: 'closed'; game: string };

export type QuizAnswer = { id: string; game: string; i: number; choice: number };

/** Kahoot-style answer tiles: a colour and a shape for each option. */
export const TILES = [
  { color: '#e21b3c', shape: '▲', name: 'red triangle' },
  { color: '#1368ce', shape: '◆', name: 'blue diamond' },
  { color: '#d89e00', shape: '●', name: 'yellow circle' },
  { color: '#26890c', shape: '■', name: 'green square' },
];

/** Points for a right answer: 500 to 1000, more the faster it came (Kahoot's rule), plus a streak bonus. */
export function pointsFor(secondsTaken: number, limit: number, streak: number): number {
  const speed = 1 - Math.min(1, Math.max(0, secondsTaken / limit)) / 2;
  return Math.round(1000 * speed) + Math.min(5, Math.max(0, streak - 1)) * 100;
}

type Handlers = {
  state?: (s: QuizState) => void;
  join?: (id: string) => void;
  answer?: (a: QuizAnswer) => void;
};

/**
 * One connection per class per page, shared: the join banner and the game
 * page can both listen without opening the channel twice.
 */
class QuizLink {
  private ch: RealtimeChannel;
  private subs = new Set<Handlers>();
  last: QuizState | null = null;
  ready: Promise<void>;
  constructor(readonly cid: string) {
    let done: () => void = () => {};
    this.ready = new Promise(r => { done = r; });
    this.ch = supabase.channel(`quiz:${cid}`, { config: { private: true, broadcast: { self: false } } });
    this.ch
      .on('broadcast', { event: 'state' }, ({ payload }) => { this.last = payload as QuizState; for (const s of this.subs) s.state?.(this.last); })
      .on('broadcast', { event: 'join' }, ({ payload }) => { if (payload?.id) for (const s of this.subs) s.join?.(payload.id); })
      .on('broadcast', { event: 'answer' }, ({ payload }) => { if (payload?.id) for (const s of this.subs) s.answer?.(payload as QuizAnswer); })
      .subscribe(status => { if (status === 'SUBSCRIBED') done(); });
  }
  on(h: Handlers) { this.subs.add(h); return () => { this.subs.delete(h); }; }
  async send(event: 'state' | 'join' | 'answer', payload: unknown) {
    await this.ready;
    if (event === 'state') this.last = payload as QuizState;
    void this.ch.send({ type: 'broadcast', event, payload });
  }
}

const links = new Map<string, QuizLink>();
export function quizLink(cid: string): QuizLink {
  let l = links.get(cid);
  if (!l) { l = new QuizLink(cid); links.set(cid, l); }
  return l;
}

// ── Sounds for the big screen (Web Audio, no files) ────────────────────────
let actx: AudioContext | null = null;
export function quizTone(kind: 'tick' | 'go' | 'reveal' | 'right' | 'wrong' | 'fanfare' | 'join') {
  try {
    actx ??= new AudioContext();
    if (actx.state === 'suspended') void actx.resume();
    const notes: Record<typeof kind, [number, number, number][]> = {
      tick: [[880, 0, 0.05]],
      go: [[523, 0, 0.1], [784, 0.1, 0.18]],
      reveal: [[392, 0, 0.12], [523, 0.12, 0.12], [659, 0.24, 0.3]],
      right: [[659, 0, 0.1], [988, 0.1, 0.25]],
      wrong: [[220, 0, 0.25]],
      fanfare: [[523, 0, 0.15], [659, 0.15, 0.15], [784, 0.3, 0.15], [1047, 0.45, 0.5]],
      join: [[1047, 0, 0.08], [1319, 0.07, 0.1]],
    };
    const t0 = actx.currentTime;
    for (const [f, at, dur] of notes[kind]) {
      const o = actx.createOscillator(), g = actx.createGain();
      o.type = kind === 'wrong' ? 'square' : 'triangle';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t0 + at);
      g.gain.exponentialRampToValueAtTime(kind === 'wrong' ? 0.06 : 0.14, t0 + at + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur);
      o.connect(g).connect(actx.destination); o.start(t0 + at); o.stop(t0 + at + dur + 0.05);
    }
  } catch { /* no sound: fine */ }
}
